import React, {useMemo} from 'react';
import {Box} from '@mui/material';
import {useAutoField} from '../../../components/AutoForm/useAutoForm';
import {FeeFragment} from '../../../schema-types/FeeFragment';
import {feeDeciderExtendedType, planFeesFieldType} from './PlanFees';
import type {modelPortfolioField} from '../RecommendModify/modifyFieldTypes';
import {themeColors} from '../../../theme/Theme';
import {useRecommendContext} from './CreateRecommendHelpers';

type selectedFeesType = {
  fp: FeeFragment | null;
  dfm: FeeFragment | null;
  product: FeeFragment | null;
};

const NonStandardFeeWarning: React.FC<{
  fees: feeDeciderExtendedType;
  formikKeyPrefixWithoutFees: string;
  activePortfolio: boolean;
}> = ({fees, formikKeyPrefixWithoutFees, activePortfolio}) => {
  const upperLimit = activePortfolio ? 1.5 : 1.45;
  const lowerLimit = activePortfolio ? 1.5 : 1.45;
  const [{value: feeIdsValue}] = useAutoField<planFeesFieldType | undefined>(
    `${formikKeyPrefixWithoutFees}.planFees`
  );

  const selected: selectedFeesType = useMemo(() => {
    const fp = fees.fpFees.fees.find((x) => x.id === feeIdsValue?.FPFeeId) || null;
    const dfm = fees.dfmFees.fees.find((x) => x.id === feeIdsValue?.DFMFeeId) || null;
    const product =
      fees.productFees.fees.find((x) => x.id === feeIdsValue?.productFeeId) || null;
    return {
      fp,
      dfm,
      product,
    };
  }, [fees, feeIdsValue]);

  const validation: 'good' | 'over' | 'under' = useMemo(() => {
    if (
      selected.dfm?.calculationBasis === 'Percentage fee' &&
      selected.fp?.calculationBasis === 'Percentage fee' &&
      selected.product?.calculationBasis === 'Percentage fee'
    ) {
      if (
        (selected.dfm?.tiers?.length || selected.dfm?.annualPercentage) &&
        (selected.fp?.tiers?.length || selected.fp?.annualPercentage) &&
        (selected.product?.tiers?.length || selected.product?.annualPercentage)
      ) {
        const fpFeeTier = selected.fp?.tiers?.[0] || Number(selected.fp.annualPercentage);

        const dfmFeeTier =
          selected.dfm?.tiers?.[0] || Number(selected.dfm.annualPercentage);

        const productTier =
          selected.product?.tiers?.[0] || Number(selected.product.annualPercentage);

        if (fpFeeTier && dfmFeeTier && productTier) {
          const percentageAdded = fpFeeTier + dfmFeeTier + productTier;
          if (percentageAdded) {
            if (percentageAdded > upperLimit) {
              return 'over';
            }
            if (percentageAdded < lowerLimit) {
              return 'under';
            }
          }
        }
      }
    }
    return 'good';
  }, [selected]);

  if (activePortfolio && validation !== 'good') {
    return (
      <Box p={2} borderRadius="2px" bgcolor={themeColors.lightWarning}>
        Warning: The total percentage fees is {validation}the standard total charge of
        1.5%.
      </Box>
    );
  }
  if (validation === 'over') {
    return (
      <Box p={2} borderRadius="2px" bgcolor={themeColors.lightWarning}>
        Warning: The total percentage fees is higher than our standard charges (
        {upperLimit}% upper boundary).
      </Box>
    );
  }
  if (validation === 'under') {
    return (
      <Box p={2} borderRadius="2px" bgcolor={themeColors.lightWarning}>
        Warning: The total percentage fees is lower than our standard charges (
        {lowerLimit}% lower boundary).
      </Box>
    );
  }
  return null;
};

export const NonStandardFeeCheck: React.FC<{
  fees: feeDeciderExtendedType;
  formikKeyPrefixWithoutFees: string;
}> = ({fees, formikKeyPrefixWithoutFees}) => {
  const {isSPPAR, isSPPDA} = useRecommendContext();
  const [{value: modelPortfolioValue}] = useAutoField<modelPortfolioField>(
    `${formikKeyPrefixWithoutFees}.model`
  );
  if (isSPPAR || isSPPDA) {
    return null;
  }
  if (!modelPortfolioValue) {
    return null;
  }
  if (typeof modelPortfolioValue === 'string') {
    return null;
  }
  if (modelPortfolioValue?.name?.toLocaleLowerCase().includes('saltus')) {
    return (
      <NonStandardFeeWarning
        fees={fees}
        formikKeyPrefixWithoutFees={formikKeyPrefixWithoutFees}
        activePortfolio={
          !modelPortfolioValue.name.toLocaleLowerCase().includes('global markets')
        }
      />
    );
  }
  return null;
};
